import { type PropsWithChildren, useCallback, useMemo, useState } from "react";
import type {
  Airport,
  Person,
  Prices,
  SeatSlice,
} from "@b2bportal/air-booking-api";
import type { SeatSegment } from "@b2bportal/air-booking-api/lib/api";
import type { GordianSeat } from "@b2bportal/air-seats-api";
import SeatsFrame from "@checkout/components/Seats/SeatsFrame";
import type { HopperSeatTotal } from "@checkout/states/Products/Flight/states/SeatSelection/context";
import { trackEvent } from "@hopper-b2b/api";
import {
  type SeatEvents,
  SeatEventTypes,
  useCheckoutSend,
} from "@hopper-b2b/checkout";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";

import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  SKIPPED_SEAT_SELECTION,
  type GordianSeatSegment,
  type SeatInfo,
  type TripDetails,
} from "@hopper-b2b/types";
import {
  ActionLink,
  BackButton,
  IconComponent,
  IconName,
  MobilePopoverCard,
  UberPriceSummaryRow,
} from "@hopper-b2b/ui";

import { useEnableDarkMode } from "@hopper-b2b/utilities";
import clsx from "clsx";
import { FlightSliceRow } from "..";
import Card from "../../../../../../assets/client/card.svg";
import CardWhite from "../../../../../../assets/client/darkMode/card-white.svg";
import PassengerIconWhite from "../../../../../../assets/client/darkMode/passenger-white.svg?react";
import PassengerIcon from "../../../../../../assets/client/passenger.svg?react";
import {
  seatMapOptions,
  styleString,
  hopperStyleString,
  hopperSeatMapOptions,
} from "../../iframeStyles";
import seatMapDarkModeStyles from "../../seatMapDarkMode.scss?inline";
import styles from "./styles.module.scss";

export interface SelectedSeatSegment {
  seats: SeatInfo[];
  departureDate: string;
  destinationCode: string;
  originCode: string;
}

interface ISeatsProps extends PropsWithChildren {
  // Connected Seats
  airports: { [key: string]: Airport } | undefined;
  cheapestSeat: Prices | undefined;
  tripDetails: TripDetails;
  seatSlices: SeatSlice[] | undefined;
  selectedSeats?: GordianSeat[];
  passengers?: Person[];
  totalSeatPricing?: number;
  seatMapHtml: string | undefined;
  selectedSeatSegments?: SelectedSeatSegment[];
  // Other
  fonts: string[];
  fontFamilies: string[];
  onNext?: () => void;
  onBack?: () => void;
  onSeatsSelected: (
    selectedSeats: GordianSeat[],
    selectedSeatSegments: GordianSeatSegment[],
    selectedSeatTotal?: HopperSeatTotal
  ) => void;
}

export const SelectedSeats = ({
  airports,
  cheapestSeat,
  tripDetails,
  seatSlices,
  selectedSeats,
  passengers,
  totalSeatPricing,
  seatMapHtml,
  selectedSeatSegments,
  fonts,
  fontFamilies,
  onNext,
  onBack,
  onSeatsSelected,
}: ISeatsProps) => {
  const { t, formatFiatCurrency, formatCurrency } = useI18nContext();
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [seatEditIndex, setSeatEditIndex] = useState(undefined);
  const send = useCheckoutSend<SeatEvents>();

  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();

  const paxWithSeatPricing = useMemo(() => {
    return passengers.map((passenger) => {
      // get the passenger's seats
      const passengerPrice = selectedSeats
        .filter((seat) => seat.person_id === passenger.id)
        .map((seat) => {
          return parseFloat(seat.price);
        })
        // combine the prices
        .reduce((prevPrice, price) => prevPrice + price, 0);
      return {
        passengerName: `${passenger.givenName} ${passenger.surname}`,
        total: passengerPrice,
      };
    });
  }, [passengers, selectedSeats]);

  const closeSeatMap = useCallback(() => {
    setShowSeatMap(false);
  }, []);

  const onEdit = useCallback(
    (seatSegment: SeatSegment) => {
      const fmap = seatSlices.flatMap((el) => el.segments);
      setSeatEditIndex(fmap.indexOf(seatSegment));
      setShowSeatMap(true);
      setIsEditMode(true);
    },
    [seatSlices]
  );

  const onSkip = useCallback(() => {
    trackEvent({
      eventName: SKIPPED_SEAT_SELECTION,
      properties: {},
    });
    send({ type: SeatEventTypes.SKIP_SEAT_SELECTION });
    onNext();
  }, [onNext, send]);

  const openPriceBreakdownHandler = useCallback(() => {
    setShowPriceBreakdown(true);
  }, []);

  const closePriceBreakdownHandler = useCallback(() => {
    setShowPriceBreakdown(false);
  }, []);

  return (
    <>
      <div className={styles.SelectedSeats}>
        {!showSeatMap ? (
          <div className="overview">
            <div className="header">
              <BackButton onClick={onBack} />
              <h3>{t("seatSelection")}</h3>
            </div>

            <div className="subheader">
              <h3>{t("seats.selectYourSeats")}</h3>
              <p className="subtext">
                <Trans
                  i18nKey={"seats.cheapestSeatPerTraveler"}
                  values={{
                    price: formatFiatCurrency({ ...cheapestSeat.fiat }),
                  }}
                  components={[<strong />]}
                />
              </p>
            </div>

            <div className="content">
              <p className="slice-header">{t("seats.outboundFlights")}</p>
              <FlightSliceRow
                slice={tripDetails.slices[0]}
                seatSlice={seatSlices[0]}
                airports={airports}
                selectedSeatSegments={selectedSeatSegments}
                passengers={passengers}
                onEdit={onEdit}
              />
              {tripDetails.slices.length > 1 ? (
                <>
                  <p className="slice-header">{t("seats.returnFlights")}</p>
                  <FlightSliceRow
                    slice={tripDetails.slices[1]}
                    seatSlice={seatSlices[1]}
                    airports={airports}
                    selectedSeatSegments={selectedSeatSegments}
                    passengers={passengers}
                    onEdit={onEdit}
                  />
                </>
              ) : null}
            </div>
            <div className="cta-container">
              {selectedSeats.length > 0 ? (
                <UberPriceSummaryRow
                  priceString={formatCurrency(totalSeatPricing)}
                  iconSrc={isDarkMode ? CardWhite : Card}
                  description={t("seats.totalCost")}
                  onClick={openPriceBreakdownHandler}
                />
              ) : null}

              <button
                className="cta"
                onClick={selectedSeats.length === 0 ? onSkip : onNext}
              >
                {selectedSeats.length === 0
                  ? t("seats.cancelSeatSelection")
                  : t("continue")}
              </button>
            </div>
          </div>
        ) : (
          <SeatsFrame
            seatMapHtml={seatMapHtml}
            seatMapOptions={seatMapOptions}
            styleString={styleString}
            segmentIndex={seatEditIndex}
            isEditMode={isEditMode}
            fonts={fonts}
            fontFamilies={fontFamilies}
            onSetOpen={setShowSeatMap}
            onCancel={closeSeatMap}
            onSeatsSelected={onSeatsSelected}
            hopperSeatMapOptions={hopperSeatMapOptions}
            hopperStyleString={
              isDarkMode
                ? hopperStyleString + seatMapDarkModeStyles
                : hopperStyleString
            }
          />
        )}
      </div>
      <MobilePopoverCard
        centered
        fullScreen
        open={showPriceBreakdown}
        onClose={closePriceBreakdownHandler}
        topRightButton={
          <ActionLink
            className="review-price-breakdown-modal-close-button"
            content={
              <IconComponent
                ariaLabel={t("close")}
                className={"close-button-icon"}
                name={IconName.Close}
              />
            }
            label={t("close")}
            onClick={closePriceBreakdownHandler}
          />
        }
        headerElement={
          <div className="review-price-breakdown-modal-header">
            {t("priceBreakdown")}
          </div>
        }
        className="review-price-breakdown-modal"
        contentClassName={clsx("review-price-breakdown-modal-content", {
          darkMode: isDarkMode,
        })}
      >
        {paxWithSeatPricing.map((pax) => (
          <div className={styles.PassengerSeatRow}>
            {isDarkMode ? (
              <PassengerIconWhite className="icon" />
            ) : (
              <PassengerIcon className="icon" />
            )}
            <div className="content">
              <p className="pax-name">{pax.passengerName}</p>
              <p className="subtitle">{t("seatSelection")}</p>
            </div>
            <p className="price">{formatCurrency(pax.total)}</p>
          </div>
        ))}
        <hr className={styles.Break} />
        <div className={styles.TotalPricing}>
          <p>{t("total")}</p>
          <p>{formatCurrency(totalSeatPricing)}</p>
        </div>
      </MobilePopoverCard>
    </>
  );
};
